import { useState } from 'react';
import { Plus, MessageSquare, Target, ListChecks } from 'lucide-react';
import { addRetrospective, addCommentToEntity, updateCommentInEntity, deleteCommentFromEntity, addReactionToComment, addTask } from '../store';
import { Retrospective, Comment, Task } from '../types';
import CreateRetrospectiveModal from '../components/CreateRetrospectiveModal';
import { CommentSection } from '../components/CommentSection';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import { generateId } from '../utils/idGenerator';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { useNavigate } from 'react-router';
import {
  RETRO_INFO_ICON_CLASS,
  RETRO_INFO_SECTION_CLASS,
  RETRO_RISK_ICON_CLASS,
  RETRO_RISK_SECTION_CLASS,
  RETRO_SUCCESS_ICON_CLASS,
  RETRO_SUCCESS_SECTION_CLASS,
  RETRO_WARNING_ICON_CLASS,
  RETRO_WARNING_SECTION_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
} from '../constants/widgetTheme';

export default function Retrospectives() {
  const navigate = useNavigate();
  const { state, loading, reload } = useApp();
  const { projects, tasks, sprints, users } = useOrganizationData(state) as {
    projects: any[];
    tasks: any[];
    sprints: any[];
    users: any[];
  };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'sprint' | 'project' | 'task'>('all');

  if (loading || !state) {
    return <div className={`p-8 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>Loading retrospectives...</div>;
  }

  if (!state.currentUser) {
    return <div className={`p-8 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>User context not loaded</div>;
  }

  const orgRetrospectives = (state.retrospectives || []).filter(
    (retro) => !retro.organizationId || retro.organizationId === state.currentOrganization?.id
  );

  const handleCreateRetrospective = async (retrospective: Retrospective) => {
    try {
      await addRetrospective(retrospective);
      if (state.currentUser.email) {
        await reload(state.currentUser.email);
      }
      setIsCreateModalOpen(false);
      toast.success('Retrospective created');
    } catch (error) {
      console.error('[Retrospectives] Failed to create retrospective:', error);
      toast.error('Failed to create retrospective');
    }
  };

  const filteredRetrospectives = filterType === 'all'
    ? orgRetrospectives
    : orgRetrospectives.filter((retro) => retro.type === filterType);

  const getRetrospectiveTitle = (retro: Retrospective) => {
    if (retro.sprintId) {
      const sprint = sprints.find((s) => s.id === retro.sprintId);
      return sprint ? `Sprint: ${sprint.name}` : 'Unknown Sprint';
    }
    if (retro.projectId) {
      const project = projects.find((p) => p.id === retro.projectId);
      return project ? `Project: ${project.name}` : 'Unknown Project';
    }
    if (retro.taskId) {
      const task = tasks.find((t) => t.id === retro.taskId);
      return task ? `Task: ${task.title}` : 'Unknown Task';
    }
    return 'General Retrospective';
  };

  const handleCreateTaskFromRetrospectiveItem = async (retro: Retrospective, actionItem: string) => {
    if (!retro.projectId) {
      toast.error('This retrospective is not linked to a project, so a task cannot be created.');
      return;
    }

    const orgId = retro.organizationId || state.currentOrganization?.id || state.currentUser.organizationId;
    if (!orgId) {
      toast.error('Organization context is missing.');
      return;
    }

    const taskId = generateId('task', retro.projectId, retro.id);
    const retrospectiveLink = `/app/retrospectives#${retro.id}`;
    const newTask: Task = {
      id: taskId,
      organizationId: orgId,
      projectId: retro.projectId,
      title: actionItem,
      description: `Created from retrospective action item in "${getRetrospectiveTitle(retro)}".`,
      type: 'Improvement',
      status: 'Not Started',
      priority: 'Medium',
      assigneeIds: [],
      parentTaskIds: [],
      subtaskIds: [],
      estimatedDays: 0,
      timeLogs: [],
      standupNotes: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      customerVisible: false,
      attachments: [],
      verificationLinks: [],
      externalLinks: [{ type: 'Retrospective', url: retrospectiveLink }],
      costItemIds: [],
      changeLogs: [],
    };

    try {
      await addTask(newTask);
      const activityComment: Comment = {
        id: generateId('comment', retro.id),
        content: `Created task ${taskId} from action item: ${actionItem}`,
        createdBy: state.currentUser.id,
        createdByName: state.currentUser.name,
        createdAt: new Date().toISOString(),
      };
      await addCommentToEntity('retrospective', retro.id, activityComment);
      toast.success('Task created from retrospective action item');
      navigate(`/task/${taskId}`);
    } catch (error) {
      console.error('[Retrospectives] Failed to create task from action item:', error);
      toast.error('Failed to create task from retrospective action item');
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 lg:p-8">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl mb-2">Retrospectives</h1>
          <p className={WIDGET_MUTED_TEXT_CLASS}>Review and improve with team retrospectives</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-5 h-5" />
          New Retrospective
        </button>
      </div>

      {/* Filter */}
      <div className={`${WIDGET_PANEL_CLASS} mb-6 p-4`}>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Filter by:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'sprint', label: 'Sprints' },
              { value: 'project', label: 'Projects' },
              { value: 'task', label: 'Tasks' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterType(option.value as any)}
                className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                  filterType === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-accent'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Retrospectives List */}
      {filteredRetrospectives.length === 0 ? (
        <div className={`${WIDGET_PANEL_CLASS} p-12 text-center`}>
          <MessageSquare className={`mx-auto mb-4 h-16 w-16 ${WIDGET_MUTED_TEXT_CLASS}`} />
          <p className={`mb-4 ${WIDGET_MUTED_TEXT_CLASS}`}>
            {orgRetrospectives.length === 0
              ? 'No retrospectives yet'
              : 'No retrospectives match your filter'}
          </p>
          {orgRetrospectives.length === 0 && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
              Create Your First Retrospective
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredRetrospectives.map((retro) => {
            const author = users.find((u) => u.id === retro.createdBy);
            return (
              <div key={retro.id} className={`${WIDGET_PANEL_CLASS} p-6`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl mb-1">{getRetrospectiveTitle(retro)}</h3>
                    <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                      Created by {author?.name || 'Unknown'} on{' '}
                      {new Date(retro.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="inline-flex rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm capitalize text-primary">
                    {retro.type}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* What Went Well */}
                  <div className={RETRO_SUCCESS_SECTION_CLASS}>
                    <div className="flex items-center gap-2 mb-3">
                      <ListChecks className={`h-5 w-5 ${RETRO_SUCCESS_ICON_CLASS}`} />
                      <h4 className={RETRO_SUCCESS_ICON_CLASS}>What Went Well</h4>
                    </div>
                    {retro.whatWentWell.length === 0 ? (
                      <p className={`text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>Nothing noted</p>
                    ) : (
                      <ul className="space-y-2">
                        {retro.whatWentWell.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-foreground/90">
                            <span className={`mt-0.5 ${RETRO_SUCCESS_ICON_CLASS}`}>✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* What Didn't Go Well */}
                  <div className={RETRO_RISK_SECTION_CLASS}>
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className={`h-5 w-5 ${RETRO_RISK_ICON_CLASS}`} />
                      <h4 className={RETRO_RISK_ICON_CLASS}>What Didn't Go Well</h4>
                    </div>
                    {retro.whatDidntGoWell.length === 0 ? (
                      <p className={`text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>Nothing noted</p>
                    ) : (
                      <ul className="space-y-2">
                        {retro.whatDidntGoWell.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-foreground/90">
                            <span className={`mt-0.5 ${RETRO_RISK_ICON_CLASS}`}>✗</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Needs Improvement */}
                  <div className={RETRO_WARNING_SECTION_CLASS}>
                    <div className="flex items-center gap-2 mb-3">
                      <Target className={`h-5 w-5 ${RETRO_WARNING_ICON_CLASS}`} />
                      <h4 className={RETRO_WARNING_ICON_CLASS}>Needs Improvement</h4>
                    </div>
                    {retro.needsImprovement?.length === 0 || !retro.needsImprovement ? (
                      <p className={`text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>Nothing noted</p>
                    ) : (
                      <ul className="space-y-2">
                        {retro.needsImprovement.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-foreground/90">
                            <span className={`mt-0.5 ${RETRO_WARNING_ICON_CLASS}`}>⚠</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Action Items - Full Width Below */}
                {retro.actionItems.length > 0 && (
                  <div className={`${RETRO_INFO_SECTION_CLASS} mt-6`}>
                    <div className="flex items-center gap-2 mb-3">
                      <ListChecks className={`h-5 w-5 ${RETRO_INFO_ICON_CLASS}`} />
                      <h4 className={RETRO_INFO_ICON_CLASS}>Action Items</h4>
                    </div>
                    <ul className="space-y-2">
                      {retro.actionItems.map((item, index) => (
                        <li key={index} className="flex items-start justify-between gap-3 text-sm text-foreground/90">
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 ${RETRO_INFO_ICON_CLASS}`}>→</span>
                            <span>{item}</span>
                          </div>
                          {retro.projectId && (
                            <button
                              onClick={() => void handleCreateTaskFromRetrospectiveItem(retro, item)}
                              className="shrink-0 rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground hover:bg-muted"
                              title="Create task from this action item"
                            >
                              Create Task
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Comment Section */}
                <div className="mt-6">
                  <CommentSection
                    comments={retro.comments || []}
                    currentUserId={state.currentUser.id}
                    currentUserName={state.currentUser.name}
                    onAddComment={async (content, parentCommentId, _includeInStandup, _includeInProjectFeed, _includeInOrgFeed, attachments) => {
                      try {
                        const newComment: Comment = {
                          id: generateId('comment', retro.id),
                          content,
                          createdBy: state.currentUser.id,
                          createdByName: state.currentUser.name,
                          createdAt: new Date().toISOString(),
                          parentCommentId,
                          attachments,
                        };
                        await addCommentToEntity('retrospective', retro.id, newComment);
                        toast.success('Comment added');
                      } catch (error) {
                        console.error('[Retrospectives] Failed to add comment:', error);
                        toast.error('Failed to add comment');
                      }
                    }}
                    onEditComment={async (commentId, newContent, attachments) => {
                      try {
                        const updates: Partial<Comment> = { content: newContent };
                        if (attachments) {
                          updates.attachments = attachments;
                        }

                        await updateCommentInEntity('retrospective', retro.id, commentId, updates);
                        toast.success('Comment updated');
                      } catch (error) {
                        console.error('[Retrospectives] Failed to update comment:', error);
                        toast.error('Failed to update comment');
                      }
                    }}
                    onDeleteComment={async (commentId) => {
                      try {
                        await deleteCommentFromEntity('retrospective', retro.id, commentId);
                        toast.success('Comment deleted');
                      } catch (error) {
                        console.error('[Retrospectives] Failed to delete comment:', error);
                        toast.error('Failed to delete comment');
                      }
                    }}
                    onAddReaction={async (commentId, emoji) => {
                      try {
                        await addReactionToComment('retrospective', retro.id, commentId, state.currentUser.id, emoji);
                      } catch (error) {
                        console.error('[Retrospectives] Failed to add reaction:', error);
                        toast.error('Failed to update reaction');
                      }
                    }}
                    placeholder="Share your thoughts on this retrospective..."
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isCreateModalOpen && (
        <CreateRetrospectiveModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateRetrospective}
          organizationId={state.currentOrganization?.id || state.currentUser.organizationId}
          currentUser={state.currentUser}
          projects={projects}
          sprints={sprints}
          tasks={tasks}
        />
      )}
    </div>
  );
}